import { DEFAULT_GAME_SETTINGS, store, } from "../app/state/store.js";
import { labelFor, registerCustomIcon, registerCustomLabel, } from "../theme/naming.js";
import { iconFor } from "../theme/icons.js";
import { loadCustomPhonemes, loadHiddenPresets, loadPresetOverrides, loadTrainedPhonemes, } from "../storage/phonemes.js";
import { getActiveLibrary } from "../storage/libraries.js";
import { escapeHtml } from "../game/util/escape.js";
const PRESET_LANES = ["hiss", "airy", "pop", "hum"];
// The authored (un-expanded) base stage and repeat count survive component
// re-mounts (e.g. play → return) so we never re-load the repeat-expanded copy
// as if it were the base pattern.
let AUTHORED_BASE = null;
let AUTHORED_REPEAT = 1;
let AUTHORED_TOTAL_BEATS = 16;
// Mirror the difficulty presets used in normal Game mode (ac-game-settings),
// restricted to the tolerance/scoring fields the designer owns.
const DIFFICULTY_PRESETS = [
    {
        id: "relaxed",
        label: "Relaxed",
        icon: "🌱",
        settings: { windowMs: 320, perfectMs: 90, basePoints: 120, comboCap: 30 },
    },
    {
        id: "normal",
        label: "Normal",
        icon: "⚖️",
        settings: { windowMs: 220, perfectMs: 45, basePoints: 100, comboCap: 20 },
    },
    {
        id: "sharp",
        label: "Sharp",
        icon: "⚡",
        settings: { windowMs: 150, perfectMs: 30, basePoints: 100, comboCap: 20 },
    },
    {
        id: "expert",
        label: "Expert",
        icon: "🔥",
        settings: { windowMs: 100, perfectMs: 20, basePoints: 90, comboCap: 15 },
    },
];
export class AcLevelDesigner extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "lanes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: [...PRESET_LANES]
        });
        Object.defineProperty(this, "percussive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "trained", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "stage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: this.freshStage()
        });
        Object.defineProperty(this, "totalBeats", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 16
        });
        Object.defineProperty(this, "snap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "selectedSustain", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "repeatCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "tuningExpanded", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "gameConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                basePoints: DEFAULT_GAME_SETTINGS.basePoints,
                comboCap: DEFAULT_GAME_SETTINGS.comboCap,
                perfectMs: DEFAULT_GAME_SETTINGS.perfectMs,
                infinite: false,
                enabled: {},
            }
        });
    }
    connectedCallback() {
        this.style.display = "block";
        this.style.color = "#fff";
        this.style.position = "relative";
        this.style.zIndex = "1";
        this.style.padding = "16px";
        this.style.minHeight = "100%";
        this.style.background = "rgba(6,8,18,.85)";
        // Prefer the authored (un-expanded) base stage that survives re-mounts,
        // so returning from a repeated play does NOT re-load the expanded copy.
        if (AUTHORED_BASE) {
            this.stage = structuredCloneStage(AUTHORED_BASE);
            this.repeatCount = AUTHORED_REPEAT;
            this.totalBeats = AUTHORED_TOTAL_BEATS;
            // Restore the store to the base (undo any transient expansion).
            store.designedStage.set(structuredCloneStage(this.stage));
        }
        else {
            const existing = store.designedStage.get();
            if (existing)
                this.stage = structuredCloneStage(existing);
        }
        void this.loadLibrary().then(() => this.render());
    }
    freshStage() {
        return {
            title: "My Stage",
            tempoBpm: 90,
            windowMs: 220,
            notes: [],
        };
    }
    async loadLibrary() {
        let activeIsDefault = true;
        let activePhonemes = [];
        try {
            const active = await getActiveLibrary();
            activeIsDefault = active.id === "default";
            activePhonemes = active.phonemes.map((p) => ({
                id: p.id,
                label: p.label,
                icon: p.icon,
                percussive: p.percussive,
                trained: p.trained,
            }));
            for (const p of active.phonemes) {
                registerCustomLabel(p.id, p.label);
                registerCustomIcon(p.id, p.icon);
            }
        }
        catch { }
        let hidden = [];
        let custom = [];
        try {
            hidden = await loadHiddenPresets();
        }
        catch { }
        try {
            custom = await loadCustomPhonemes();
        }
        catch { }
        let overrides = {};
        try {
            overrides = await loadPresetOverrides();
        }
        catch { }
        this.percussive.clear();
        if (activeIsDefault) {
            const visiblePresets = PRESET_LANES.filter((id) => !hidden.includes(id));
            this.lanes = [...visiblePresets, ...custom.map((p) => p.id)];
            for (const id of visiblePresets) {
                const perc = overrides[id]?.percussive ?? id === "pop";
                if (perc)
                    this.percussive.add(id);
            }
            for (const p of custom) {
                if (p.percussive)
                    this.percussive.add(p.id);
            }
        }
        else {
            this.lanes = activePhonemes.map((p) => p.id);
            for (const p of activePhonemes) {
                if (p.percussive)
                    this.percussive.add(p.id);
            }
        }
        let trained;
        if (activeIsDefault) {
            let trainedDefs = [];
            try {
                trainedDefs = await loadTrainedPhonemes();
            }
            catch { }
            trained = new Set(trainedDefs.filter((p) => p.trained).map((p) => p.id));
        }
        else {
            trained = new Set(activePhonemes.filter((p) => p.trained).map((p) => p.id));
        }
        this.trained = trained;
        for (const id of this.lanes) {
            if (this.gameConfig.enabled[id] === undefined) {
                this.gameConfig.enabled[id] = trained.has(id);
            }
            else if (this.gameConfig.enabled[id] && !trained.has(id)) {
                this.gameConfig.enabled[id] = false;
            }
        }
    }
    isPercussive(id) {
        return this.percussive.has(id);
    }
    activePresetId() {
        for (const preset of DIFFICULTY_PRESETS) {
            const s = preset.settings;
            if (this.stage.windowMs === s.windowMs &&
                this.gameConfig.perfectMs === s.perfectMs &&
                this.gameConfig.basePoints === s.basePoints &&
                this.gameConfig.comboCap === s.comboCap) {
                return preset.id;
            }
        }
        return null;
    }
    noteAt(clusterId, beat) {
        return (this.stage.notes.find((n) => n.clusterId === clusterId && Math.abs(n.beat - beat) < 1e-6) ?? null);
    }
    coveringNote(clusterId, beat, startNote) {
        for (const n of this.stage.notes) {
            if (n.clusterId !== clusterId)
                continue;
            if (n === startNote)
                continue;
            if (n.sustainBeats <= 0)
                continue;
            const end = n.beat + n.sustainBeats;
            if (beat > n.beat - 1e-6 && beat < end - 1e-6) {
                return n;
            }
        }
        return null;
    }
    toggleNote(clusterId, beat) {
        const existing = this.noteAt(clusterId, beat);
        if (existing) {
            this.stage.notes = this.stage.notes.filter((n) => n !== existing);
        }
        else {
            const sustainBeats = this.isPercussive(clusterId)
                ? 0
                : this.selectedSustain;
            this.stage.notes.push({
                clusterId,
                beat,
                sustainBeats,
            });
        }
        this.persist();
        this.render();
    }
    clearNotes() {
        this.stage.notes = [];
        this.persist();
        this.render();
    }
    persist() {
        store.designedStage.set(structuredCloneStage(this.stage));
        AUTHORED_BASE = structuredCloneStage(this.stage);
        AUTHORED_REPEAT = this.repeatCount;
        AUTHORED_TOTAL_BEATS = this.totalBeats;
    }
    play() {
        if (this.stage.notes.length === 0) {
            console.warn("[ac-level-designer] no notes placed; not playing");
            return;
        }
        // Snapshot the authored base BEFORE writing the expanded stage so a
        // return to the designer restores the base (not the expansion).
        AUTHORED_BASE = structuredCloneStage(this.stage);
        AUTHORED_REPEAT = this.repeatCount;
        AUTHORED_TOTAL_BEATS = this.totalBeats;
        // Push the designer's scoring/tolerance config into the shared game
        // settings so the played stage honors the chosen difficulty/tuning.
        store.gameSettings.update((s) => ({
            ...s,
            basePoints: this.gameConfig.basePoints,
            comboCap: this.gameConfig.comboCap,
            perfectMs: this.gameConfig.perfectMs,
            infinite: this.gameConfig.infinite,
        }));
        // Persist ONLY the expanded stage transiently for the game to read.
        // The base stage remains in-memory here; connectedCallback should not
        // re-load the expanded copy (see restore guard below).
        store.designedStage.set(this.expandedStage());
        this.dispatchEvent(new CustomEvent("designer-play", { bubbles: true }));
    }
    /**
     * Build a copy of the stage whose note pattern is repeated `repeatCount`
     * times back to back. The primary use case is practising a sequence of
     * phonemes (a word) multiple times in a row. The editable grid always shows
     * only the base block; this expansion is only used for play/export so the
     * base pattern is never mutated.
     */
    expandedStage() {
        const reps = Math.max(1, Math.round(this.repeatCount));
        const base = this.stage.notes.map((n) => ({ ...n }));
        if (reps <= 1 || base.length === 0) {
            return structuredCloneStage(this.stage);
        }
        // Use the full authored length as the repeat block so trailing empty
        // space is preserved as an intentional break between repeats (rather
        // than trimming to the last note).
        const blockBeats = Math.max(this.snap, this.totalBeats);
        const expanded = [];
        for (let r = 0; r < reps; r++) {
            const offset = r * blockBeats;
            for (const n of base) {
                expanded.push({ ...n, beat: n.beat + offset });
            }
        }
        console.info(`[ac-level-designer] expanded repeat ×${reps} ` +
            `(block=${blockBeats} beats → ${expanded.length} notes)`);
        return {
            title: this.stage.title,
            tempoBpm: this.stage.tempoBpm,
            windowMs: this.stage.windowMs,
            notes: expanded,
        };
    }
    render() {
        const theme = "child";
        const beats = this.totalBeats;
        const cols = Math.round(beats / this.snap);
        const gridLanes = this.lanes.filter((id) => this.gameConfig.enabled[id] ?? false);
        const laneRows = gridLanes
            .map((id) => {
            const glyph = escapeHtml(iconFor(id, theme));
            const label = escapeHtml(labelFor(id, theme));
            const cells = [];
            for (let c = 0; c < cols; c++) {
                const beat = c * this.snap;
                const note = this.noteAt(id, beat);
                const covering = this.coveringNote(id, beat, note);
                const on = !!note;
                const covered = !note && !!covering;
                const beatBoundary = Math.abs(beat - Math.round(beat)) < 1e-6;
                const barNote = note ?? covering;
                const bg = on
                    ? "#22d3ee"
                    : covered
                        ? "#1c8a9c"
                        : beatBoundary
                            ? "rgba(255,255,255,.06)"
                            : "rgba(255,255,255,.02)";
                const isBarEnd = !!barNote &&
                    barNote.sustainBeats > 0 &&
                    Math.abs(beat - (barNote.beat + barNote.sustainBeats - this.snap)) <
                        1e-6;
                const radius = on
                    ? barNote && barNote.sustainBeats > 0
                        ? "8px 0 0 8px"
                        : "8px"
                    : covered && isBarEnd
                        ? "0 8px 8px 0"
                        : "0";
                const sustainTxt = note && note.sustainBeats > 0
                    ? `<span style="font-size:9px;opacity:.85">${note.sustainBeats}b</span>`
                    : "";
                cells.push(`<div data-cell data-cluster="${id}" data-beat="${beat}"
                            style="flex:1;min-width:22px;height:34px;
                            border-right:1px solid rgba(255,255,255,.05);
                            display:flex;align-items:center;justify-content:center;
                            cursor:pointer;pointer-events:auto;background:${bg};
                            border-radius:${radius}">
                            ${sustainTxt}
                          </div>`);
            }
            return `<div style="display:flex;align-items:center;gap:8px;margin:4px 0">
                    <div style="flex:0 0 130px;display:flex;align-items:center;gap:6px">
                      <span style="font-size:16px">${glyph}</span>
                      <span style="font-size:12px;opacity:.85">${label}</span>
                    </div>
                    <div style="flex:1;display:flex;border:1px solid #2a2f4a;
                        border-radius:8px;overflow:hidden;min-width:${cols * 22}px">${cells.join("")}</div>
                  </div>`;
        })
            .join("");
        const beatRuler = (() => {
            const marks = [];
            for (let c = 0; c < cols; c++) {
                const beat = c * this.snap;
                const isBeat = Math.abs(beat - Math.round(beat)) < 1e-6;
                marks.push(`<div style="flex:1;min-width:22px;text-align:center;
                            font-size:10px;opacity:${isBeat ? ".7" : ".25"}">
                            ${isBeat ? Math.round(beat) : "·"}</div>`);
            }
            return `<div style="display:flex;align-items:center;gap:8px;margin:0 0 2px">
                    <div style="flex:0 0 130px"></div>
                    <div style="flex:1;display:flex;min-width:${cols * 22}px">${marks.join("")}</div>
                  </div>`;
        })();
        const sustainOptions = [0, 1, 2, 4, 8]
            .map((b) => `<button data-sustain="${b}"
                            style="pointer-events:auto;cursor:pointer;padding:4px 10px;
                            border-radius:8px;font-size:12px;font-weight:600;
                            border:1px solid ${this.selectedSustain === b ? "#22d3ee" : "#2a2f4a"};
                            background:${this.selectedSustain === b ? "#12324a" : "#12162a"};
                            color:#fff">${b === 0 ? "tap" : `${b}b`}</button>`)
            .join("");
        const phonemeToggles = this.lanes
            .map((id) => {
            const on = this.gameConfig.enabled[id] ?? true;
            const glyph = escapeHtml(iconFor(id, theme));
            const label = escapeHtml(labelFor(id, theme));
            const trained = this.trained.has(id);
            const trainMark = trained
                ? ""
                : `<span style="font-size:10px;color:#fbbf24;margin-left:4px">untrained</span>`;
            return `<div data-phoneme-toggle="${id}"
                    style="display:flex;align-items:center;gap:6px;padding:6px 10px;
                    border-radius:8px;
                    cursor:${trained ? "pointer" : "not-allowed"};
                    pointer-events:${trained ? "auto" : "none"};
                    border:2px solid ${on ? "#22d3ee" : "#2a2f4a"};
                    background:${on ? "#12324a" : "#12162a"};
                    opacity:${trained ? (on ? "1" : ".6") : ".4"}">
                    <span style="font-size:15px">${glyph}</span>
                    <span style="font-size:12px">${label}</span>
                    ${trainMark}
                    <span style="margin-left:4px;font-size:11px">${on ? "✓" : "○"}</span>
                  </div>`;
        })
            .join("");
        const activePreset = this.activePresetId();
        const presetButtons = DIFFICULTY_PRESETS.map((preset) => {
            const on = activePreset === preset.id;
            return `<div data-preset="${preset.id}"
                    style="flex:1;min-width:80px;display:flex;flex-direction:column;
                    align-items:center;gap:4px;padding:10px 8px;border-radius:10px;
                    cursor:pointer;pointer-events:auto;
                    border:2px solid ${on ? "#22d3ee" : "#2a2f4a"};
                    background:${on ? "#12324a" : "#12162a"}">
                    <span style="font-size:20px">${preset.icon}</span>
                    <span style="font-size:12px;font-weight:600">${preset.label}</span>
                  </div>`;
        }).join("");
        const configBlock = `
          <div style="margin-top:14px;padding:12px;border-radius:12px;
              background:rgba(18,22,42,.6);border:1px solid #2a2f4a">
            <div style="font-size:13px;font-weight:700;margin-bottom:8px">
              Difficulty</div>
            <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px">
              ${presetButtons}
            </div>
            <div data-tuning-toggle
                style="display:flex;align-items:center;gap:8px;cursor:pointer;
                pointer-events:auto;font-size:13px;font-weight:700;
                padding:4px 0;user-select:none">
              <span style="transition:transform .15s ease;
                  transform:rotate(${this.tuningExpanded ? "90deg" : "0deg"})">▶</span>
              <span>Advanced tuning</span>
            </div>
            <div style="display:${this.tuningExpanded ? "flex" : "none"};
                flex-wrap:wrap;gap:16px;align-items:flex-end;margin-top:8px">
              <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                <span style="opacity:.7">Base points <b>${this.gameConfig.basePoints}</b></span>
                <input data-cfg-basepoints type="range" min="50" max="300" step="10"
                    value="${this.gameConfig.basePoints}"
                    style="pointer-events:auto;width:150px"/>
              </label>
              <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                <span style="opacity:.7">Combo cap <b>${this.gameConfig.comboCap}x</b></span>
                <input data-cfg-combocap type="range" min="5" max="40" step="1"
                    value="${this.gameConfig.comboCap}"
                    style="pointer-events:auto;width:150px"/>
              </label>
              <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                <span style="opacity:.7">Perfect window <b>${this.gameConfig.perfectMs}ms</b></span>
                <input data-cfg-perfectms type="range" min="15" max="120" step="5"
                    value="${this.gameConfig.perfectMs}"
                    style="pointer-events:auto;width:150px"/>
              </label>
              <div data-cfg-infinite
                  style="display:flex;align-items:center;gap:8px;padding:8px 12px;
                  border-radius:10px;cursor:pointer;pointer-events:auto;
                  border:2px solid ${this.gameConfig.infinite ? "#22d3ee" : "#2a2f4a"};
                  background:${this.gameConfig.infinite ? "#12324a" : "#12162a"}">
                <span style="font-size:12px">Infinite game</span>
                <span style="font-size:13px">${this.gameConfig.infinite ? "✓" : "○"}</span>
              </div>
            </div>
            <div style="font-size:12px;opacity:.7;margin:10px 0 6px">
              Enabled sounds</div>
            <div style="display:flex;flex-wrap:wrap;gap:8px">${phonemeToggles}</div>
          </div>`;
        this.innerHTML = `
              <div style="max-width:900px;margin:0 auto">
                <h1 style="margin:0 0 4px">Level Designer</h1>
                <p style="opacity:.8;margin:0 0 16px">
                  Place notes on the beat grid for each sound, then play your
                  stage. Click a cell to add/remove a note. Sustained sounds use
                  the selected hold length; percussive sounds are always taps.
                </p>

                <div style="display:flex;flex-wrap:wrap;gap:16px;align-items:flex-end;
                    margin-bottom:14px;padding:12px;border-radius:12px;
                    background:rgba(18,22,42,.6);border:1px solid #2a2f4a">
                  <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                    <span style="opacity:.7">Title</span>
                    <input data-title type="text" value="${escapeHtml(this.stage.title)}"
                        style="pointer-events:auto;padding:6px 8px;border-radius:8px;
                        border:1px solid #2a2f4a;background:#12162a;color:#fff;
                        font-size:13px;width:160px"/>
                  </label>
                  <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                    <span style="opacity:.7">Tempo <b>${this.stage.tempoBpm} bpm</b></span>
                    <input data-tempo type="range" min="40" max="200" step="1"
                        value="${this.stage.tempoBpm}"
                        style="pointer-events:auto;width:150px"/>
                  </label>
                  <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                    <span style="opacity:.7">Hit window <b>${this.stage.windowMs}ms</b></span>
                    <input data-window type="range" min="80" max="400" step="10"
                        value="${this.stage.windowMs}"
                        style="pointer-events:auto;width:150px"/>
                  </label>
                  <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                    <span style="opacity:.7">Length <b>${this.totalBeats} beats</b></span>
                    <div style="display:flex;align-items:center;gap:6px">
                      <button data-beats-dec
                          style="pointer-events:auto;cursor:pointer;width:28px;height:28px;
                          border-radius:8px;border:1px solid #2a2f4a;background:#12162a;
                          color:#fff;font-size:16px;font-weight:700">−</button>
                      <input data-beats type="range" min="4" max="256" step="1"
                          value="${this.totalBeats}"
                          style="pointer-events:auto;width:150px"/>
                      <button data-beats-inc
                          style="pointer-events:auto;cursor:pointer;width:28px;height:28px;
                          border-radius:8px;border:1px solid #2a2f4a;background:#12162a;
                          color:#fff;font-size:16px;font-weight:700">+</button>
                    </div>
                  </label>
                  <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                    <span style="opacity:.7">Repeat <b>×${this.repeatCount}</b></span>
                    <div style="display:flex;align-items:center;gap:6px">
                      <button data-repeat-dec
                          style="pointer-events:auto;cursor:pointer;width:28px;height:28px;
                          border-radius:8px;border:1px solid #2a2f4a;background:#12162a;
                          color:#fff;font-size:16px;font-weight:700">−</button>
                      <input data-repeat type="range" min="1" max="16" step="1"
                          value="${this.repeatCount}"
                          style="pointer-events:auto;width:150px"/>
                      <button data-repeat-inc
                          style="pointer-events:auto;cursor:pointer;width:28px;height:28px;
                          border-radius:8px;border:1px solid #2a2f4a;background:#12162a;
                          color:#fff;font-size:16px;font-weight:700">+</button>
                    </div>
                  </label>
                  <label style="display:flex;flex-direction:column;gap:4px;font-size:12px">
                    <span style="opacity:.7">Snap</span>
                    <select data-snap
                        style="pointer-events:auto;padding:6px 8px;border-radius:8px;
                        border:1px solid #2a2f4a;background:#12162a;color:#fff;font-size:13px">
                      <option value="1" ${this.snap === 1 ? "selected" : ""}>1 beat</option>
                      <option value="0.5" ${this.snap === 0.5 ? "selected" : ""}>1/2 beat</option>
                      <option value="0.25" ${this.snap === 0.25 ? "selected" : ""}>1/4 beat</option>
                    </select>
                  </label>
                </div>

                <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
                  <span style="font-size:12px;opacity:.7">Sustain for new notes:</span>
                  ${sustainOptions}
                </div>

                <div style="overflow-x:auto;padding:10px;border-radius:12px;
                    background:rgba(10,12,24,.6);border:1px solid #2a2f4a">
                  ${beatRuler}
                  ${laneRows}
                </div>
                ${configBlock}

                <div style="display:flex;gap:10px;margin-top:16px">
                  <button data-play
                      style="pointer-events:auto;cursor:pointer;padding:10px 20px;
                      border-radius:12px;border:none;background:#22d3ee;color:#0a0d1c;
                      font-size:15px;font-weight:800">▶ Play stage</button>
                  <button data-clear
                      style="pointer-events:auto;cursor:pointer;padding:10px 16px;
                      border-radius:12px;border:1px solid #f87171;background:#1a1f3a;
                      color:#fff;font-size:14px;font-weight:600">Clear</button>
                  <button data-export
                      style="pointer-events:auto;cursor:pointer;padding:10px 16px;
                      border-radius:12px;border:1px solid #22d3ee;background:#1a1f3a;
                      color:#fff;font-size:14px;font-weight:600">Copy JSON</button>
                  <button data-import
                      style="pointer-events:auto;cursor:pointer;padding:10px 16px;
                      border-radius:12px;border:1px solid #22d3ee;background:#1a1f3a;
                      color:#fff;font-size:14px;font-weight:600">Paste JSON</button>
                  <button data-download
                      style="pointer-events:auto;cursor:pointer;padding:10px 16px;
                      border-radius:12px;border:1px solid #4ade80;background:#1a1f3a;
                      color:#fff;font-size:14px;font-weight:600">Download JSON</button>
                  <span style="align-self:center;font-size:12px;opacity:.7">
                    ${this.stage.notes.length} note(s)</span>
                  <span data-status style="align-self:center;font-size:12px;
                      font-weight:600"></span>
                </div>
              </div>`;
        this.wire();
    }
    wire() {
        this.querySelectorAll("[data-cell]").forEach((cell) => {
            cell.addEventListener("click", () => {
                const cluster = cell.getAttribute("data-cluster");
                const beat = Number(cell.getAttribute("data-beat"));
                if (cluster)
                    this.toggleNote(cluster, beat);
            });
        });
        const titleInp = this.querySelector("[data-title]");
        titleInp?.addEventListener("input", () => {
            this.stage.title = titleInp.value;
            this.persist();
        });
        const tempoInp = this.querySelector("[data-tempo]");
        tempoInp?.addEventListener("input", () => {
            this.stage.tempoBpm = Number(tempoInp.value);
            this.persist();
            this.render();
        });
        const windowInp = this.querySelector("[data-window]");
        windowInp?.addEventListener("input", () => {
            this.stage.windowMs = Number(windowInp.value);
            this.persist();
            this.render();
        });
        const beatsInp = this.querySelector("[data-beats]");
        beatsInp?.addEventListener("input", () => {
            this.totalBeats = Number(beatsInp.value);
            this.persist();
            this.render();
        });
        this.querySelector("[data-beats-inc]")?.addEventListener("click", () => {
            this.totalBeats = Math.min(256, this.totalBeats + 1);
            this.persist();
            this.render();
        });
        this.querySelector("[data-beats-dec]")?.addEventListener("click", () => {
            this.totalBeats = Math.max(4, this.totalBeats - 1);
            this.persist();
            this.render();
        });
        const repeatInp = this.querySelector("[data-repeat]");
        repeatInp?.addEventListener("input", () => {
            this.repeatCount = Number(repeatInp.value);
            this.persist();
            this.render();
        });
        this.querySelector("[data-repeat-inc]")?.addEventListener("click", () => {
            this.repeatCount = Math.min(16, this.repeatCount + 1);
            this.persist();
            this.render();
        });
        this.querySelector("[data-repeat-dec]")?.addEventListener("click", () => {
            this.repeatCount = Math.max(1, this.repeatCount - 1);
            this.persist();
            this.render();
        });
        const snapSel = this.querySelector("[data-snap]");
        snapSel?.addEventListener("change", () => {
            this.snap = Number(snapSel.value);
            this.render();
        });
        this.querySelectorAll("[data-sustain]").forEach((btn) => {
            btn.addEventListener("click", () => {
                this.selectedSustain = Number(btn.getAttribute("data-sustain"));
                this.render();
            });
        });
        this.querySelector("[data-play]")?.addEventListener("click", () => this.play());
        this.querySelector("[data-clear]")?.addEventListener("click", () => this.clearNotes());
        this.querySelector("[data-export]")?.addEventListener("click", () => void this.exportToClipboard());
        this.querySelector("[data-import]")?.addEventListener("click", () => void this.importFromClipboard());
        this.querySelector("[data-download]")?.addEventListener("click", () => this.downloadJson());
        const basePts = this.querySelector("[data-cfg-basepoints]");
        basePts?.addEventListener("input", () => {
            this.gameConfig.basePoints = Number(basePts.value);
            this.render();
        });
        const comboCap = this.querySelector("[data-cfg-combocap]");
        comboCap?.addEventListener("input", () => {
            this.gameConfig.comboCap = Number(comboCap.value);
            this.render();
        });
        const perfectMs = this.querySelector("[data-cfg-perfectms]");
        perfectMs?.addEventListener("input", () => {
            this.gameConfig.perfectMs = Number(perfectMs.value);
            this.render();
        });
        this.querySelector("[data-cfg-infinite]")?.addEventListener("click", () => {
            this.gameConfig.infinite = !this.gameConfig.infinite;
            this.render();
        });
        this.querySelectorAll("[data-phoneme-toggle]").forEach((el) => {
            const id = el.getAttribute("data-phoneme-toggle");
            if (!id)
                return;
            el.addEventListener("click", () => {
                if (!this.trained.has(id))
                    return;
                const cur = this.gameConfig.enabled[id] ?? true;
                this.gameConfig.enabled[id] = !cur;
                this.render();
            });
        });
        this.querySelectorAll("[data-preset]").forEach((el) => {
            const id = el.getAttribute("data-preset");
            const preset = DIFFICULTY_PRESETS.find((p) => p.id === id);
            if (!preset)
                return;
            el.addEventListener("click", () => {
                this.stage.windowMs = preset.settings.windowMs;
                this.gameConfig.perfectMs = preset.settings.perfectMs;
                this.gameConfig.basePoints = preset.settings.basePoints;
                this.gameConfig.comboCap = preset.settings.comboCap;
                this.persist();
                this.render();
            });
        });
        this.querySelector("[data-tuning-toggle]")?.addEventListener("click", () => {
            this.tuningExpanded = !this.tuningExpanded;
            this.render();
        });
    }
    downloadJson() {
        const stage = this.expandedStage();
        const payload = {
            ...stage,
            gameConfig: this.gameConfig,
        };
        const json = JSON.stringify(payload, null, 2);
        const safeName = (this.stage.title || "stage")
            .trim()
            .replace(/[^a-z0-9_-]+/gi, "-")
            .replace(/^-+|-+$/g, "")
            .toLowerCase() || "stage";
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${safeName}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        console.info(`[ac-level-designer] downloaded stage '${safeName}.json' ` +
            `(${this.stage.notes.length} notes)`);
        this.flashStatus(`Downloaded ${safeName}.json ✓`, "#4ade80");
    }
    async exportToClipboard() {
        const json = JSON.stringify(this.expandedStage(), null, 2);
        try {
            await navigator.clipboard.writeText(json);
            console.info("[ac-level-designer] copied stage JSON to clipboard");
            this.flashStatus("Copied stage to clipboard ✓", "#4ade80");
        }
        catch (err) {
            console.warn("[ac-level-designer] clipboard write failed", err);
            this.flashStatus("Clipboard blocked — see console", "#f87171");
            console.info("[ac-level-designer] stage JSON:\n" + json);
        }
    }
    async importFromClipboard() {
        let text;
        try {
            text = await navigator.clipboard.readText();
        }
        catch (err) {
            console.warn("[ac-level-designer] clipboard read failed", err);
            this.flashStatus("Clipboard read blocked", "#f87171");
            return;
        }
        try {
            const parsed = JSON.parse(text);
            if (!Array.isArray(parsed.notes)) {
                throw new Error("missing notes array");
            }
            const notes = parsed.notes
                .filter((n) => !!n &&
                typeof n.clusterId === "string" &&
                Number.isFinite(n.beat) &&
                Number.isFinite(n.sustainBeats))
                .map((n) => ({
                clusterId: n.clusterId,
                beat: n.beat,
                sustainBeats: n.sustainBeats,
                pitchHz: n.pitchHz ?? null,
            }));
            this.stage = {
                title: typeof parsed.title === "string" ? parsed.title : "Imported Stage",
                tempoBpm: Number.isFinite(parsed.tempoBpm)
                    ? parsed.tempoBpm
                    : 90,
                windowMs: Number.isFinite(parsed.windowMs)
                    ? parsed.windowMs
                    : 220,
                notes,
            };
            const maxBeat = notes.reduce((m, n) => Math.max(m, n.beat + n.sustainBeats), 0);
            this.totalBeats = Math.max(this.totalBeats, Math.ceil((maxBeat + 4) / 4) * 4);
            this.persist();
            console.info(`[ac-level-designer] imported stage with ${notes.length} note(s)`);
            this.flashStatus("Imported stage ✓", "#4ade80");
            this.render();
        }
        catch (err) {
            console.warn("[ac-level-designer] failed to parse stage JSON", err);
            this.flashStatus("Invalid JSON on clipboard", "#f87171");
        }
    }
    flashStatus(msg, color) {
        const el = this.querySelector("[data-status]");
        if (!el)
            return;
        el.textContent = msg;
        el.style.color = color;
        window.setTimeout(() => {
            if (el.textContent === msg)
                el.textContent = "";
        }, 3000);
    }
}
function structuredCloneStage(s) {
    return {
        title: s.title,
        tempoBpm: s.tempoBpm,
        windowMs: s.windowMs,
        notes: s.notes.map((n) => ({ ...n })),
    };
}
customElements.define("ac-level-designer", AcLevelDesigner);
